import React, { useState } from 'react';
import {
  SalvFieldHeader,
  SalvProgressCircle,
  SalvTable,
  SalvTablePaginationType,
  SalvTooltipWrapper,
} from '@salvapp/ui-components';
import { InternalApiScoreResult, InternalApiSortDirection, InternalApiTransactionByIdsQuery } from '../api-internal';
import { PaginationState, SortingState, Updater } from '@tanstack/react-table';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import '../search/TransactionSearchResults.scss';
import { getOrderByAndDirection } from '../common/sort';
import useTransactionColumns from '../transaction/table/useTransactionColumns';
import { getTransactionPath } from '../navigation/helper';
import { useNavigate } from 'react-router';
import { defaultSearchQuery } from '../search/TransactionSearch';
import { useQueryTransactionByIds } from './transactionQueryActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import ExternalLink from '../common/ExternalLink';

export const TRANSACTION_LIMIT = 100;

interface TransactionSearchResultsProps {
  score: InternalApiScoreResult;
  defaultExpanded?: boolean;
}

const AlertTransactions = ({ score, defaultExpanded }: TransactionSearchResultsProps) => {
  const navigate = useNavigate();

  const [transactionQuery, setTransactionQuery] = useState<InternalApiTransactionByIdsQuery>({
    ids: score.relatedTransactions || [],
    size: 20,
    orderBy: defaultSearchQuery.orderBy,
    direction: defaultSearchQuery.direction,
    page: defaultSearchQuery.page,
  });

  const setPagination = (state: PaginationState) => {
    setTransactionQuery((prev) => {
      return {
        ...prev,
        page: state.pageIndex,
      };
    });
  };

  const sortingState: SortingState = [
    {
      id: transactionQuery.orderBy || '',
      desc: transactionQuery.direction === InternalApiSortDirection.DESC,
    },
  ];

  const setSorting = (updaterOrValue: Updater<SortingState>) => {
    const { orderBy, direction } = getOrderByAndDirection(updaterOrValue, sortingState);

    setTransactionQuery((prev) => {
      return {
        ...prev,
        orderBy,
        direction,
      };
    });
  };

  const { data, isFetching, isError } = useQueryTransactionByIds(transactionQuery);
  const transactionsPage = data || {
    number: 0,
    size: 0,
    total: 0,
    content: [],
  };

  const {
    columns,
    isError: isColumnsError,
    isPending: isColumnsPending,
  } = useTransactionColumns({
    transactions: transactionsPage.content,
    enableSorting: true,
  });

  if (!score.includeRelatedTransactions) {
    return (
      <SalvFieldHeader
        label={
          <div className={'no-related-transactions'}>
            <FontAwesomeIcon icon={faCircleInfo} style={{ color: 'var(--text-medium)', marginRight: '8px' }} />
            <div style={{ color: 'var(--text-medium)', flexShrink: 0 }}>Scenario</div>
            &nbsp;
            <SalvTooltipWrapper tooltip={score?.reason} delay={1000}>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{score?.reason}</div>
            </SalvTooltipWrapper>
            &nbsp;
            <div style={{ color: 'var(--text-medium)', flexShrink: 0 }}>
              is not configured to return contributing transactions
            </div>
          </div>
        }
      />
    );
  } else if (isColumnsError || isError) {
    return <DataLoadingErrorFallback message={'Could not load Transactions'} />;
  } else if (isColumnsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Transactions'} />;
  }

  return (
    <SalvTable
      isLoading={isFetching}
      data={transactionsPage.content}
      columns={columns}
      pagination={{
        type: SalvTablePaginationType.PAGINATION,
        total: transactionsPage.total,
        state: {
          pageIndex: transactionsPage.number,
          pageSize: transactionsPage.size,
        },
        setPagination,
      }}
      sorting={{
        state: sortingState,
        setSorting: setSorting,
      }}
      search
      onSearch={(value) =>
        setTransactionQuery((prev) => {
          return {
            ...prev,
            query: value.trim() ? value.trim() : undefined,
            page: 0,
          };
        })
      }
      className={'transaction-search-results m-b-24'}
      rowClick={(row) => navigate(getTransactionPath(row.original.id))}
      title={
        <div style={{ display: 'flex' }}>
          <div className={'text-details'} style={{ flexShrink: 0 }}>
            Scenario:
          </div>
          &nbsp;
          <SalvTooltipWrapper tooltip={score?.reason} delay={1000}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{score?.reason}</div>
          </SalvTooltipWrapper>
          &nbsp;
          <div className={'high-priority'} style={{ flexShrink: 0, marginRight: '5px' }}>
            ({transactionsPage.total}
            {transactionsPage.total === TRANSACTION_LIMIT ? '+' : ''})
          </div>
        </div>
      }
      actions={
        transactionsPage.total === TRANSACTION_LIMIT && (
          <span className='warning-message' style={{ borderRadius: '4px', padding: '6px' }}>
            <FontAwesomeIcon icon={faTriangleExclamation} className='m-l-4' />
            <span>Showing up to {TRANSACTION_LIMIT} transactions returned by the scenario.</span>
          </span>
        )
      }
      emptyMessage={
        <div data-testid='empty-message'>
          No transactions returned by the scenario query. If transactions were expected, make sure scenario query
          correctly returns transaction IDs. Read more{' '}
          <ExternalLink link='https://help.salv.com/en/articles/523371-contributing-transactions' title='here' />.
        </div>
      }
      defaultExpanded={defaultExpanded}
      expandable={true}
      expandOldStyle={true}
      actionsAtBottom={true}
    />
  );
};

export default AlertTransactions;
